import React, { useState } from "react";
import { Link } from "react-router-dom";

import { useFeature } from "components/OrganizationFeatures";
import { useRoutes } from "utils/routes";

import styles from "./style.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCheckCircle,
  faExclamation,
  faExclamationCircle,
} from "@fortawesome/pro-solid-svg-icons";
import Button from "components/Button";
import { ExpandToggle } from "components/Icons";
import {
  useDashboardCheckUpQuery,
  DashboardCheckUpQuery,
} from "./gql/Query.generated";
import Loading from "components/Loading";

type IssueGroupType =
  DashboardCheckUpQuery["getCheckUpStatus"]["issueGroups"][number];

type Props = {
  databaseId: string;
  organizationSlug: string;
};

const CheckUp = ({ databaseId, organizationSlug }: Props) => {
  const { databaseChecks, organizationSubscriptionComparePlans } = useRoutes();
  const enabled = useFeature("checkUp");
  const [expanded, setExpanded] = useState(false);

  const { data, loading, error } = useDashboardCheckUpQuery({
    variables: { databaseId },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const { critical, issueGroups } = data.getCheckUpStatus;

  const nonOkIssueGroups = issueGroups.filter((ig) => ig.status != "okay");
  const okIssueGroups = issueGroups.filter((ig) => ig.status === "okay");

  return (
    <div>
      <h4>
        <Link to={databaseChecks(databaseId)}>
          {critical ? (
            <>
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="!w-[20px] align-middle mr-1 text-[#FF0000]"
              />
              <span className="align-middle text-[#FF0000]">
                Critical issues found
              </span>
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="!w-[20px] align-middle mr-1 text-[#0F792B]"
              />
              <span className="align-middle text-[#0F792B]">
                Up &amp; Running
              </span>
            </>
          )}
        </Link>
      </h4>
      {enabled ? (
        <>
          {nonOkIssueGroups.length > 0 ? (
            nonOkIssueGroups.map((ig) => (
              <IssueGroupLine
                issueGroup={ig}
                databaseId={databaseId}
                key={ig.message}
              />
            ))
          ) : (
            <div className={styles.checkUpItemOkay}>
              <FontAwesomeIcon
                className="!w-[20px] mr-1 inline-block text-center"
                icon={faCheck}
              />
              All checks are passing
            </div>
          )}
          {okIssueGroups.length > 0 && (
            <>
              <Button
                bare
                onClick={() => setExpanded((currExp) => !currExp)}
                className={styles.checkUpItem}
              >
                <span className="!w-[20px] mr-1 inline-block" />
                {expanded ? "Hide passing checks" : "Show passing checks"}
                <ExpandToggle
                  expanded={expanded}
                  className="!w-[20px] mr-1 inline-block text-center"
                />
              </Button>
              {expanded &&
                okIssueGroups.map((ig) => (
                  <IssueGroupLine
                    issueGroup={ig}
                    databaseId={databaseId}
                    key={ig.message}
                  />
                ))}
            </>
          )}
        </>
      ) : (
        <div className={styles.checkUpUnavailable}>
          <p>
            <strong>Check-Up</strong> is not included in your current plan.
            <br />
            <Link to={organizationSubscriptionComparePlans(organizationSlug)}>
              Learn more about our Production plans.
            </Link>
          </p>
        </div>
      )}
    </div>
  );
};

const IssueGroupLine = ({
  issueGroup: ig,
  databaseId,
}: {
  issueGroup: IssueGroupType;
  databaseId: string;
}) => {
  const { databaseCheck, databaseChecks } = useRoutes();
  const checkOkay = ig.status === "okay";

  return (
    <Link
      className={getClassName(ig.status)}
      to={
        ig.checkName === "all"
          ? databaseChecks(databaseId)
          : databaseCheck(databaseId, ig.groupName, ig.checkName)
      }
    >
      <FontAwesomeIcon
        className="!w-[20px] mr-1 inline-block text-center"
        icon={checkOkay ? faCheck : faExclamation}
      />
      {ig.message} {!checkOkay && `(${ig.count})`}
    </Link>
  );
};

const CHECK_UP_STATUS_TO_STYLE = {
  critical: styles.checkUpItemCritical,
  warning: styles.checkUpItemWarning,
  info: styles.checkUpItemInfo,
  okay: styles.checkUpItemOkay,
};

function getClassName(status: string) {
  if (status in CHECK_UP_STATUS_TO_STYLE) {
    return CHECK_UP_STATUS_TO_STYLE[
      status as keyof typeof CHECK_UP_STATUS_TO_STYLE
    ];
  }
  return undefined;
}

export default CheckUp;
